import apiClient from './client';
import { hash as e2eeHash } from '../utils/e2eeCrypto';

/** All classifier-derived facets in one dict. Comes from the
 *  `document_facets` EAV table on the backend, keyed by classifier
 *  `key`. The set of keys is now admin-managed (see the classifiers
 *  table) instead of a hardcoded enum, so this is a fully dynamic
 *  Record<string, string[]>. Missing categories arrive as `[]` (or
 *  may be omitted entirely). */
export type DocumentFacets = Record<string, string[]>;

// ---------------------------------------------------------------------------
// Medical Timeline (structured JSON, persisted on family_members.timeline as
// JSONB). Mirrors `backend/app/schemas/timeline.py`. The frontend renders
// this via `<MedicalTimeline data={...} />` (react-chrono based component).
// ---------------------------------------------------------------------------

export type TimelineEventCategory =
  | 'lab'
  | 'imaging'
  | 'clinical'
  | 'procedure'
  | 'medication'
  | 'diagnosis'
  | 'vaccination'
  | 'consultation'
  | 'other';

export type TimelineEventSeverity = 'info' | 'notable' | 'important' | 'critical';

export type TimelineEventStatus =
  | 'normal'
  | 'abnormal'
  | 'improving'
  | 'worsening'
  | 'stable'
  | 'unknown';

export type TimelineDatePrecision = 'day' | 'month' | 'year' | 'approx';

export interface TimelineAnalyte {
  name: string;
  value?: string | null;
  unit?: string | null;
  /** Free-form Romanian label ("Scăzut", "Ridicat", "Normal", "Critic", …). */
  status?: string | null;
}

export interface TimelineEvent {
  /** Stable id ("<ISO date>-<slug>"); used as the react-chrono row key. */
  id: string;
  /** ISO 8601 date (`YYYY-MM-DD`) or full datetime; server sorts ascending. */
  date: string;
  date_precision: TimelineDatePrecision;
  title: string;
  summary: string;
  category: TimelineEventCategory;
  severity: TimelineEventSeverity;
  status?: TimelineEventStatus | null;
  /** All ids here are guaranteed to belong to the member's document set
   *  — the backend drops hallucinated references before persisting. */
  source_document_ids: number[];
  specialty?: string | null;
  body_regions: string[];
  analytes: TimelineAnalyte[];
  related_conditions: string[];
  tags: string[];
}

export interface TimelineDateRange {
  /** ISO date of the earliest event. */
  from?: string | null;
  /** ISO date of the latest event. */
  to?: string | null;
}

export interface MedicalTimeline {
  member_id: number;
  /** ISO 8601 UTC timestamp recorded server-side at generation time. */
  generated_at: string;
  total_events: number;
  date_range: TimelineDateRange;
  /** 3-5 Romanian one-liners summarising the whole timeline. */
  highlights: string[];
  events: TimelineEvent[];
}

/** A single physical file inside a (possibly grouped) MedicalDocument.
 *  Bulk uploads always have `file_count === 1` and `files[0]`; group
 *  uploads have one row per uploaded file, ordered by `file_index`.
 *
 *  `is_encrypted` is always `true` for documents uploaded after
 *  migration 0011 (always-on E2EE). It's surfaced so UI consumers can
 *  reliably branch on the flag instead of inferring encryption from
 *  the absence of a `storage_mode === 'server'` value (the `storage_mode`
 *  column was dropped). */
export interface DocumentFile {
  id: number;
  /** 0-based position inside the parent document; preserves the order
   *  the user dropped/reordered files in the upload modal. */
  file_index: number;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  is_encrypted: boolean;
}

export interface MedicalDocument {
  id: number;
  family_member_id: number;
  /** Top-level convenience copy of `files[0].original_filename` for
   *  bulk docs; for grouped docs it's the user-set group title (falls
   *  back to the first file's name on the backend). */
  filename: string;
  original_filename: string;
  /** Top-level summary type — for grouped docs this is the dominant
   *  file type or "group". UI generally prefers iterating `files[]`. */
  file_type: string;
  /** Sum of all `files[*].size_bytes`. */
  file_size: number;
  upload_date: string;
  document_date: string | null;
  /** Document-level type labels mirrored from the `document_type`
   *  classifier output. The same values are also present in
   *  {@link facets}.document_type (EAV source of truth); this column
   *  is the convenience copy carried in list / detail responses so
   *  the card / detail UI can render type pills without joining
   *  facets every time. Empty / null = unclassified. */
  document_types: string[] | null;
  /** Free-form description of the document. Sourced from
   *  `classification.document.notes` and refreshed on every
   *  re-classification — manual edits via the metadata modal stick
   *  only until the next classifier run. Renders as the card
   *  description on the documents list and as a labelled block on
   *  the detail page. */
  notes: string | null;
  /** Number of physical files attached to this document. `1` for bulk
   *  uploads, `N` for grouped uploads. Drives whether the UI should
   *  render a per-file list and a ZIP-download tooltip. */
  file_count: number;
  /** Per-file rows from the `document_files` table, ordered by
   *  `file_index`. Always present (length === `file_count`). */
  files: DocumentFile[];
  /** Latest OCR (member list only, from GET /member/:id) */
  ocr_processing_status?: string | null;
  /** Unified pipeline stage (queue → OCR → summary → classification) */
  pipeline_status?: string | null;
  summary_status?: string | null;
  classification_status?: string | null;
  /** All classifier-derived facets, keyed by classifier name. */
  facets?: DocumentFacets | null;
  status?: string | null;
}

export interface UploadFileError {
  filename: string;
  message: string;
}

export interface UploadResponse {
  /** N documents for bulk mode (one per file), 1 document for group
   *  mode (containing N files). Always non-null but may be empty if
   *  every file in the request errored. */
  documents: MedicalDocument[];
  /** Per-file failures that didn't block the rest of the batch. */
  errors?: UploadFileError[];
}

/** Per-file user envelope: wraps the AES DEK for a single recipient
 *  user. In the always-E2EE world every file carries one envelope
 *  per user who needs decrypt access (the uploader + everyone the
 *  member is shared with). */
export interface UploadEnvelope {
  user_id: number;
  /** Base64-encoded RSA-OAEP-wrapped DEK bytes. */
  wrapped_dek: string;
}

/** Per-file share-link envelope: wraps the AES DEK for a share-link
 *  token. The server only ever sees the SHA-256 hash of the token
 *  (not the raw token), so the link hash is what's stored / matched.
 *  Recipients arriving via the share URL have the raw token and can
 *  re-derive the AES key. See `wrapDekBytesForLink` /
 *  `unwrapDekBytesForLink` in `utils/e2eeCrypto`. */
export interface UploadLinkEnvelope {
  /** Lowercase hex SHA-256 of the raw share-link token. Matches
   *  the format used by `share_invites.token_hash` server-side
   *  (`hashlib.sha256(token).hexdigest()`). */
  link_token_hash: string;
  /** Base64-encoded AES-GCM-wrapped DEK (ciphertext + IV
   *  concatenated, or whatever the W-backend contract pins). */
  wrapped_dek: string;
}

/** Per-file metadata that travels alongside the ciphertext blob.
 *  Order matches the order of `files` in the multipart payload.
 *  See `POST /api/documents/upload` always-E2EE contract. */
export interface UploadFileMeta {
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  /** Base64 of the 12-byte AES-GCM IV used to encrypt the file. */
  iv: string;
  envelopes: UploadEnvelope[];
  /** Per-active-share-link envelopes. Empty when there are no active
   *  share links for the member (the uploader rarely has the raw
   *  tokens at upload time — usually empty and materialised later by
   *  the `ensureLinkEnvelopesForMemberInvite` flow). */
  link_envelopes: UploadLinkEnvelope[];
  /** Browser-OCR markdown (REQUIRED when `ocr_mode === 'client'`,
   *  must be `null` when `ocr_mode === 'server'`). */
  ocr_markdown: string | null;
  ocr_confidence: number | null;
  ocr_language: string | null;
  pii_anonymized: boolean;
}

export interface UploadFilePayload {
  /** Ciphertext blob produced by `encryptFileE2EE`. The plaintext
   *  bytes are NEVER allowed in this field. */
  blob: Blob;
  /** Original filename (used for multipart filename + audit). */
  filename: string;
}

/** OCR-mode toggle exposed in the upload modal's "Setări avansate"
 *  disclosure. Pinned per the W-backend contract:
 *   - `server`: send plaintext bytes alongside the ciphertext so the
 *     server can run OCR once and discard the plaintext immediately.
 *     `filesMeta[*].ocr_markdown` MUST be `null`.
 *   - `client`: run OCR in-browser; only ciphertext leaves the
 *     device. `filesMeta[*].ocr_markdown` MUST be non-null. */
export type UploadOcrMode = 'server' | 'client';

export interface UploadDocumentArgs {
  memberId: number;
  /** Ciphertext blobs, one per file, in the same order as `filesMeta`. */
  files: UploadFilePayload[];
  /** Plaintext bytes, REQUIRED when `ocrMode === 'server'`. Same
   *  length / order as `files`; ignored entirely when
   *  `ocrMode === 'client'` (omit the field or pass an empty
   *  array). The server forwards these to the ephemeral OCR
   *  service and drops them immediately after extraction. */
  filesPlain?: UploadFilePayload[];
  /** Per-file metadata (filename, mime, iv, envelopes, link
   *  envelopes, OCR outputs). Must be the same length & order as
   *  `files`. */
  filesMeta: UploadFileMeta[];
  /** OCR pipeline selection. See {@link UploadOcrMode}. */
  ocrMode: UploadOcrMode;
  /** When true, all files are stored as a single grouped document
   *  with shared metadata. When false (default), each file becomes
   *  its own independent MedicalDocument. */
  group: boolean;
  /** Used as `original_filename` for the grouped document; ignored
   *  in bulk mode. */
  title?: string;
  notes?: string;
  onProgress?: (progress: number) => void;
}

/** Parsed value of the `X-Edosar-Encryption` JSON header attached to
 *  every encrypted-download response. Carries the IV + the per-user
 *  wrapped DEK so the client can decrypt the streamed ciphertext in
 *  one round-trip (no separate metadata endpoint to hit). */
export interface EncryptionHeaderMaterial {
  /** Base64-encoded AES-GCM IV (12 bytes). */
  iv: string;
  /** Base64-encoded RSA-OAEP-wrapped DEK bytes for the requesting user. */
  wrapped_dek: string;
}

/** Result of `documentsApi.downloadEncryptedFile` — ciphertext bytes +
 *  the matching decryption material lifted from the response header,
 *  plus the original filename parsed from `Content-Disposition`. */
export interface EncryptedDownload {
  ciphertext: ArrayBuffer;
  iv: string;
  wrapped_dek: string;
  filename: string | null;
}

// ---------------------------------------------------------------------------
// Per-file E2EE envelope metadata (always-E2EE, post-migration 0011).
// All envelopes are now scoped to a `document_file` row; bulk uploads
// surface a single-file array, group uploads surface one entry per file.
// ---------------------------------------------------------------------------

/** One row of `document_file_envelopes` for the requesting user. */
export interface E2eeFileEnvelopeMetadata {
  file_id: number;
  file_index: number;
  /** Base64 of the 12-byte AES-GCM IV used to encrypt the file. */
  iv: string;
  /** Base64-encoded RSA-OAEP-wrapped DEK bytes for the requesting user. */
  wrapped_dek: string;
}

/** Response of `GET /api/documents/{id}/e2ee` — per-file user
 *  envelopes scoped to the requesting account. Used by ShareAccept's
 *  re-wrap loop and any future "rotate keys" flow. */
export interface E2eeDocumentMetadata {
  document_id: number;
  file_count: number;
  files: E2eeFileEnvelopeMetadata[];
}

/** Response of `GET /api/documents/{id}/e2ee/link?token_hash=…` —
 *  per-file link envelopes matching a specific share-link token.
 *  Identical shape to {@link E2eeDocumentMetadata}; the DEKs here
 *  are wrapped for the link key derived from the raw token, not for
 *  a user public key. */
export interface E2eeLinkMetadata {
  document_id: number;
  file_count: number;
  files: E2eeFileEnvelopeMetadata[];
}

/** Walks all (document_id, file_id) pairs for a member. Used by
 *  ShareAccept materialisation + the owner-side
 *  `ensureLinkEnvelopesForMemberInvite` flow to know what to rewrap. */
export interface MemberE2eeDocumentRef {
  id: number;
  file_count: number;
  files: { id: number; file_index: number }[];
}

export interface MemberE2eeIdsResponse {
  documents: MemberE2eeDocumentRef[];
}

/** Body of `POST /api/documents/{id}/e2ee/link-envelopes` — bulk
 *  upsert of per-file link envelopes for a single token. */
export interface UpsertE2eeLinkEnvelopeBody {
  /** Raw share-link token; the client derives the hash before send. */
  token: string;
  files: { file_id: number; wrapped_dek: string }[];
}

/** Body of `DELETE /api/documents/{id}/e2ee/link-envelopes` — when
 *  `file_ids` is omitted the server deletes every link envelope for
 *  the token across the document's files. */
export interface DeleteE2eeLinkEnvelopeBody {
  token: string;
  file_ids?: number[];
}

/** Body of `POST /api/documents/{id}/e2ee/envelopes` — bulk upsert
 *  of per-file user envelopes (one row per `(file_id, user_id)`). */
export interface UpsertE2eeEnvelopeBody {
  files: { file_id: number; user_id: number; wrapped_dek: string }[];
}

export interface OCRResult {
  id: number;
  document_id: number;
  version: number;
  markdown_text: string | null;
  processing_date: string;
  confidence_score: number | null;
  processing_status: string;
  pipeline_status?: string | null;
  summary: string | null;
  summary_status: string | null;
  summary_error: string | null;
  classification: DocumentClassification | null;
  classification_status: string | null;
  classification_error: string | null;
  page_count: number | null;
  table_count: number | null;
}

// ---------------------------------------------------------------------------
// Classifier output (stored JSON on OCR).
//
// The classifier registry is admin-managed, so the *shape* of this payload
// is now data-driven: every active classifier writes its values to its own
// `json_path` inside the JSON, but which classifiers exist depends entirely
// on the seed the admin loaded. We model this with a loose
// `Record<string, unknown>` plus a handful of structural fields the
// pipeline always emits (version, document metadata), and we keep the
// medical-shaped extras (analytes/panels/risk/tags/imaging) as OPTIONAL
// hints — present only when the active classifier preset emits them.
// ---------------------------------------------------------------------------

export interface ClassificationAnalyte {
  name?: string;
  value?: string | number | null;
  unit?: string | null;
  reference_range?: string | null;
  status?: string;
}

export interface ClassificationRisk {
  global_risk_level?: string;
  severity_score?: number;
  abnormal_analyte_count?: number;
  critical_analyte_count?: number;
  derived?: boolean;
}

export interface ClassificationTagGroups {
  technical_tags?: string[];
  clinical_tags?: string[];
  biomarker_tags?: string[];
}

export interface DocumentClassification {
  /** Schema version the classifier service wrote this payload under. */
  classification_version?: string;
  /** Free-form bag of document-scoped metadata the LLM fills in
   *  (language, confidence, notes, care_context, source_type,
   *  structure_type, …). UI consumers should access individual fields
   *  defensively because the active classifier preset decides what
   *  actually ends up here. */
  document?: Record<string, unknown>;
  /** Optional ISO timestamp authored by the classifier when it spots a
   *  document date inside the text. */
  document_date?: string | null;

  // --- Medical-shaped extras (optional). Present only when the active
  // classifier preset emits them; UI must handle missing fields cleanly.
  analytes?: ClassificationAnalyte[];
  panels?: Array<Record<string, unknown>>;
  clinical_flags?: string[];
  risk?: ClassificationRisk;
  tags?: ClassificationTagGroups;
  search_tags?: string[];
  /** Top-level hoisted copy of `document.imaging.modalities`. */
  imaging_modalities?: string[];
  /** Top-level hoisted copy of `document.imaging.body_regions`. */
  imaging_body_regions?: string[];

  // Catch-all for any admin-defined classifier values that end up at the
  // top level of the payload (the classifier registry decides via
  // `json_path`).
  [k: string]: unknown;
}

export interface DocumentWithOCR {
  document: MedicalDocument;
  ocr_result: OCRResult | null;
  family_id: number;
  family_name: string;
  member_name: string;
}

export interface DocumentUploadData {
  family_member_id: number;
  document_date?: string;
  document_types?: string[];
  notes?: string;
}

export interface MedicalDocumentUpdatePayload {
  original_filename?: string;
  document_date?: string | null;
  document_types?: string[] | null;
  notes?: string | null;
}

/** Query params for GET /api/documents/member/:id.
 *
 *  - `q`, `file_type`, `pipeline` operate on upload-time fields.
 *  - `document_types` filters via Postgres array overlap on the
 *    classifier-derived `document_types` column — pass one or more
 *    values, the server matches docs whose array overlaps any of
 *    them.
 *  - `facets` is a dict of `<classifier_key> -> value`; each non-empty
 *    entry becomes a `facet_<classifier_key>=<value>` query param at
 *    call time. Classifier keys are admin-managed; the frontend
 *    discovers them via `useClassifiersConfig()`. */
export interface MemberDocumentsQuery {
  q?: string;
  file_type?: string;
  pipeline?: string;
  document_types?: string[];
  facets?: Record<string, string>;
}

export interface MemberDocumentFilterOptions {
  document_count: number;
  file_types: string[];
  /** Distinct values present in any document's `document_types`
   *  array (the classifier-derived type list, flattened + deduped).
   *  Drives the filter dropdown; pick one (or more) and pass as
   *  `document_types` query params. */
  document_types: string[];
  /** Distinct values per classifier across the member's documents,
   *  keyed by classifier `key`. The set of keys is admin-managed, so
   *  this is a fully dynamic Record<string, string[]>. */
  facets: Record<string, string[]>;
}

/** Slim row used by the post-login Dashboard's "Activitate recentă"
 *  feed. Cross-member view: one row per most-recently-uploaded
 *  document the caller can access (own families + family-shared +
 *  member-shared). Backed by `GET /api/documents/recent`. */
export interface RecentDocumentItem {
  id: number;
  member_id: number;
  member_name: string;
  family_id: number;
  family_name: string;
  original_filename: string;
  upload_date: string;
  document_date: string | null;
  document_types: string[] | null;
  file_count: number;
  pipeline_status: string | null;
}

/** Lowercase hex SHA-256 of a raw share-link token. Matches the
 *  format `share_invites.token_hash` uses server-side
 *  (`hashlib.sha256(token).hexdigest()`), so the same hash is
 *  accepted by both the upload validator and the per-file
 *  `/e2ee/link*` endpoints. Re-exported from `e2eeCrypto.hash`. */
const sha256Hex = (input: string): Promise<string> => e2eeHash.sha256Hex(input);

function memberDocumentsParams(params?: MemberDocumentsQuery): URLSearchParams | undefined {
  if (!params) return undefined;
  const out = new URLSearchParams();
  if (params.q && params.q.trim()) out.append('q', params.q.trim());
  if (params.file_type && params.file_type.trim()) out.append('file_type', params.file_type.trim());
  if (params.pipeline && params.pipeline.trim()) out.append('pipeline', params.pipeline.trim());
  for (const v of params.document_types ?? []) {
    const s = (v ?? '').trim();
    if (s !== '') out.append('document_types', s);
  }
  if (params.facets) {
    for (const [classifierKey, value] of Object.entries(params.facets)) {
      if (value === undefined || value === null) continue;
      const s = String(value).trim();
      if (s !== '') out.append(`facet_${classifierKey}`, s);
    }
  }
  return Array.from(out.keys()).length ? out : undefined;
}

export const documentsApi = {
  /**
   * Upload one or more *encrypted* files to `/api/documents/upload`.
   *
   * Always-E2EE contract (W-backend pinned):
   * - `files` carries ciphertext blobs from `encryptFileE2EE`. The
   *   final on-disk representation is always encrypted.
   * - `files_plain` carries the original plaintext bytes; REQUIRED
   *   when `ocrMode === 'server'` (server runs OCR once + drops the
   *   plaintext immediately), FORBIDDEN otherwise.
   * - `files_meta` is a JSON-encoded array (same order as `files`)
   *   containing per-file `iv`, per-user `envelopes`, per-share-link
   *   `link_envelopes`, and the OCR outputs. In `client` mode every
   *   entry MUST have `ocr_markdown !== null`; in `server` mode
   *   every entry MUST have `ocr_markdown === null`.
   *
   * Bulk vs group behaviour is unchanged:
   * - Bulk  (`group: false`): N files → N MedicalDocuments.
   * - Group (`group: true`) : N files → 1 MedicalDocument with N
   *   file rows inside (ordered like the input).
   *
   * Returns `{ documents, errors? }`; per-file failures land in
   * `errors[]` and don't fail the request as a whole.
   */
  uploadDocument: async (args: UploadDocumentArgs): Promise<UploadResponse> => {
    if (args.files.length !== args.filesMeta.length) {
      throw new Error('files and filesMeta must have the same length');
    }
    if (args.ocrMode === 'server') {
      if (!args.filesPlain || args.filesPlain.length !== args.files.length) {
        throw new Error(
          'ocrMode="server" requires filesPlain[] to be the same length as files[]'
        );
      }
      if (args.filesMeta.some((m) => m.ocr_markdown !== null)) {
        throw new Error('ocrMode="server" requires every filesMeta[].ocr_markdown === null');
      }
    } else {
      if (args.filesPlain && args.filesPlain.length > 0) {
        throw new Error('ocrMode="client" forbids filesPlain[]');
      }
      if (args.filesMeta.some((m) => m.ocr_markdown === null)) {
        throw new Error('ocrMode="client" requires every filesMeta[].ocr_markdown !== null');
      }
    }

    const formData = new FormData();
    formData.append('member_id', args.memberId.toString());
    formData.append('group', args.group ? 'true' : 'false');
    formData.append('ocr_mode', args.ocrMode);
    if (args.title) formData.append('title', args.title);
    if (args.notes) formData.append('notes', args.notes);
    for (const f of args.files) {
      formData.append('files', f.blob, f.filename);
    }
    if (args.ocrMode === 'server' && args.filesPlain) {
      for (const f of args.filesPlain) {
        formData.append('files_plain', f.blob, f.filename);
      }
    }
    formData.append('files_meta', JSON.stringify(args.filesMeta));

    const response = await apiClient.post<UploadResponse>('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (args.onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          args.onProgress(progress);
        }
      },
    });
    return response.data;
  },

  upsertClientOcr: async (
    documentId: number,
    payload: { markdown_text: string; confidence_score?: number | null; ocr_metadata?: Record<string, unknown> | null }
  ): Promise<void> => {
    await apiClient.post(`/api/documents/${documentId}/ocr/client`, payload);
  },

  getMemberDocuments: async (
    memberId: number,
    params?: MemberDocumentsQuery
  ): Promise<MedicalDocument[]> => {
    const response = await apiClient.get(`/api/documents/member/${memberId}`, {
      params: memberDocumentsParams(params),
    });
    return response.data;
  },

  /** Cross-member recent feed for the post-login Dashboard. Returns
   *  the most-recently-uploaded documents the caller can access
   *  across own + shared families/members, sorted by `upload_date`
   *  desc. Server caps `limit` at 50. */
  getRecentDocuments: async (limit: number = 15): Promise<RecentDocumentItem[]> => {
    const response = await apiClient.get<RecentDocumentItem[]>('/api/documents/recent', {
      params: { limit },
    });
    return response.data;
  },

  getMemberDocumentFilterOptions: async (memberId: number): Promise<MemberDocumentFilterOptions> => {
    const response = await apiClient.get(`/api/documents/member/${memberId}/filter-options`);
    return response.data;
  },

  getDocument: async (documentId: number): Promise<DocumentWithOCR> => {
    const response = await apiClient.get(`/api/documents/${documentId}`);
    return response.data;
  },

  updateDocument: async (
    documentId: number,
    data: MedicalDocumentUpdatePayload
  ): Promise<MedicalDocument> => {
    const response = await apiClient.patch(`/api/documents/${documentId}`, data);
    return response.data;
  },

  getDocumentSummary: async (documentId: number, language: string = 'ro'): Promise<{ summary: string }> => {
    const response = await apiClient.get(`/api/documents/${documentId}/summary`, {
      params: { language },
    });
    return response.data;
  },

  getMemberTimeline: async (
    memberId: number,
    language: string = 'ro',
  ): Promise<{ timeline: MedicalTimeline }> => {
    const response = await apiClient.get(`/api/documents/member/${memberId}/timeline`, {
      params: { language },
    });
    return response.data;
  },

  getMemberInsights: async (memberId: number, language: string = 'ro'): Promise<{ insights: string }> => {
    const response = await apiClient.get(`/api/documents/member/${memberId}/insights`, {
      params: { language },
    });
    return response.data;
  },

  deleteDocument: async (documentId: number): Promise<void> => {
    await apiClient.delete(`/api/documents/${documentId}`);
  },

  rerunOCR: async (documentId: number): Promise<{ message: string; document_id: number }> => {
    const response = await apiClient.post(`/api/documents/${documentId}/rerun-ocr`, null, {
      params: { ocr_only: true },
    });
    return response.data;
  },

  /** OCR + summary + classification (same as initial upload pipeline). */
  rerunFullProcessing: async (documentId: number): Promise<{ message: string; document_id: number }> => {
    const response = await apiClient.post(`/api/documents/${documentId}/rerun-ocr`, null, {
      params: { ocr_only: false },
    });
    return response.data;
  },

  /**
   * Re-run OCR for E2EE documents by providing the *decrypted* original bytes.
   * The backend forwards bytes to the OCR service ephemeral endpoint and does not persist them.
   */
  rerunOcrWithOriginalFile: async (
    documentId: number,
    args: { bytes: Uint8Array; filename: string; mime?: string; ocrOnly: boolean }
  ): Promise<{ message: string; document_id: number }> => {
    const fd = new FormData();
    // Copy into a fresh Uint8Array backed by a plain ArrayBuffer (avoids SharedArrayBuffer typing issues).
    const bytes = Uint8Array.from(args.bytes);
    const blob = new Blob([bytes], { type: args.mime || 'application/octet-stream' });
    fd.append('original_file', blob, args.filename || `document-${documentId}`);
    const response = await apiClient.post(`/api/documents/${documentId}/rerun-ocr`, fd, {
      params: { ocr_only: args.ocrOnly },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  regenerateSummary: async (
    documentId: number,
    language: string = 'ro'
  ): Promise<{ summary: string | null; summary_status: string | null; summary_error: string | null }> => {
    const response = await apiClient.post(`/api/documents/${documentId}/regenerate-summary`, null, {
      params: { language },
    });
    return response.data;
  },

  classifyDocument: async (
    documentId: number
  ): Promise<{ classification: DocumentClassification | null; classification_status: string | null; classification_error: string | null }> => {
    const response = await apiClient.post(`/api/documents/${documentId}/classify`);
    return response.data;
  },

  /**
   * Download a single file from a grouped MedicalDocument.
   *
   * Hits `GET /api/documents/{id}/files/{file_id}/download` and
   * triggers a browser save with the file's original filename
   * (parsed from `Content-Disposition` when present, otherwise
   * derived from the `MedicalDocument.files[]` entry the caller
   * already has).
   */
  downloadFile: async (
    documentId: number,
    fileId: number,
    fallbackFilename?: string
  ): Promise<void> => {
    const r = await apiClient.get(
      `/api/documents/${documentId}/files/${fileId}/download`,
      { responseType: 'blob' }
    );
    const blob = r.data as Blob;
    const cd = (r.headers['content-disposition'] || r.headers['Content-Disposition'] || '') as string;
    let filename = fallbackFilename || `document-${documentId}-file-${fileId}`;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (match?.[1]) {
      try {
        filename = decodeURIComponent(match[1]);
      } catch {
        filename = match[1];
      }
    }
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    }
  },

  /**
   * Fetch one encrypted file as ciphertext + decryption material.
   *
   * Calls `GET /api/documents/{documentId}/download` (for single-file
   * documents) or `GET /api/documents/{documentId}/files/{fileId}/download`
   * (for files inside a grouped document). The server streams the
   * ciphertext as `application/octet-stream` and pins the per-file IV
   * and the requesting user's wrapped DEK into the
   * `X-Edosar-Encryption` JSON header.
   *
   * Throws if the header is missing or doesn't parse — that signals
   * either a backend regression or a permission mismatch (server
   * refusing to attach an envelope the user can't decrypt).
   *
   * NOTE: callers MUST handle the multi-file 409 case (where the
   * single-file `/download` endpoint refuses grouped documents) before
   * invoking this. The frontend currently does that by checking
   * `document.file_count > 1` and using `fileId` instead.
   */
  downloadEncryptedFile: async (
    documentId: number,
    fileId?: number
  ): Promise<EncryptedDownload> => {
    const url =
      fileId != null
        ? `/api/documents/${documentId}/files/${fileId}/download`
        : `/api/documents/${documentId}/download`;
    const r = await apiClient.get(url, { responseType: 'arraybuffer' });
    const headerRaw =
      (r.headers['x-edosar-encryption'] as string | undefined) ||
      (r.headers['X-Edosar-Encryption'] as string | undefined);
    if (!headerRaw) {
      throw new Error('Encrypted download response missing X-Edosar-Encryption header');
    }
    let parsed: EncryptionHeaderMaterial;
    try {
      parsed = JSON.parse(headerRaw) as EncryptionHeaderMaterial;
    } catch (e) {
      throw new Error(`Malformed X-Edosar-Encryption header: ${String(e)}`);
    }
    if (!parsed.iv || !parsed.wrapped_dek) {
      throw new Error('X-Edosar-Encryption header missing iv or wrapped_dek');
    }
    const cd =
      (r.headers['content-disposition'] as string | undefined) ||
      (r.headers['Content-Disposition'] as string | undefined) ||
      '';
    let filename: string | null = null;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (match?.[1]) {
      try {
        filename = decodeURIComponent(match[1]);
      } catch {
        filename = match[1];
      }
    }
    return {
      ciphertext: r.data as ArrayBuffer,
      iv: parsed.iv,
      wrapped_dek: parsed.wrapped_dek,
      filename,
    };
  },

  /**
   * Re-wrap a DEK for a newly-authorised user on a specific file.
   *
   * Used when a member is shared with a new user *after* the original
   * upload created envelopes only for the uploader. The frontend
   * unwraps the DEK with the uploader's key, RSA-OAEP-wraps it for
   * each missing user, then POSTs one envelope per file via this
   * helper. The server validates `user_id` against the file's
   * member's authorised-user set.
   */
  upsertFileEnvelope: async (
    documentId: number,
    fileId: number,
    payload: { user_id: number; wrapped_dek: string }
  ): Promise<void> => {
    await apiClient.post(
      `/api/documents/${documentId}/files/${fileId}/envelopes`,
      payload
    );
  },

  /**
   * Per-file user-envelope metadata for the requesting user. Walks
   * every `document_file` row of the document and surfaces the
   * caller's envelope ({iv, wrapped_dek}) plus the file_index so
   * callers can stitch results back to specific files. Used by the
   * ShareAccept materialiser and the owner-side rewrap flow.
   */
  getE2eeMetadata: async (documentId: number): Promise<E2eeDocumentMetadata> => {
    const response = await apiClient.get<E2eeDocumentMetadata>(
      `/api/documents/${documentId}/e2ee`
    );
    return response.data;
  },

  /**
   * Per-file LINK-envelope metadata matching the provided share
   * token. The client SHA-256-hashes the token locally (the server
   * only ever stores hashes) and sends `token_hash` as a query
   * param; the response shape mirrors {@link getE2eeMetadata}.
   *
   * Used by `ShareAccept`: the recipient holds the raw token from
   * the URL, unwraps each file's DEK with `unwrapDekBytesForLink`,
   * then re-wraps for their own RSA public key via
   * {@link upsertE2eeEnvelope}.
   */
  getE2eeLinkMetadata: async (
    documentId: number,
    token: string
  ): Promise<E2eeLinkMetadata> => {
    const tokenHash = await sha256Hex(token);
    const response = await apiClient.get<E2eeLinkMetadata>(
      `/api/documents/${documentId}/e2ee/link`,
      { params: { token_hash: tokenHash } }
    );
    return response.data;
  },

  /**
   * Bulk-upsert per-file LINK envelopes for a single share-link
   * token. Used both at upload time (when active share links exist
   * for the member) and by the owner-side
   * `ensureLinkEnvelopesForMemberInvite` flow right after creating
   * a new invite (the only moment the owner holds the raw token).
   */
  upsertE2eeLinkEnvelope: async (
    documentId: number,
    body: UpsertE2eeLinkEnvelopeBody
  ): Promise<void> => {
    const link_token_hash = await sha256Hex(body.token);
    await apiClient.post(
      `/api/documents/${documentId}/e2ee/link-envelopes`,
      { link_token_hash, files: body.files }
    );
  },

  /**
   * Delete the LINK envelopes matching a token (optionally scoped
   * to a subset of `file_ids`). Called by `ShareAccept` after it
   * has successfully materialised the per-user envelopes so the
   * link rows can be dropped immediately.
   */
  deleteE2eeLinkEnvelope: async (
    documentId: number,
    body: DeleteE2eeLinkEnvelopeBody
  ): Promise<void> => {
    const link_token_hash = await sha256Hex(body.token);
    await apiClient.delete(
      `/api/documents/${documentId}/e2ee/link-envelopes`,
      { data: { link_token_hash, file_ids: body.file_ids } }
    );
  },

  /**
   * Bulk-upsert per-file USER envelopes across all files of a
   * document. Used by ShareAccept right after re-wrapping the
   * DEKs unwrapped from link envelopes. The per-file
   * {@link upsertFileEnvelope} above remains for one-off rewraps;
   * this is the multi-file form ShareAccept needs to avoid an
   * N-roundtrip storm.
   */
  upsertE2eeEnvelope: async (
    documentId: number,
    body: UpsertE2eeEnvelopeBody
  ): Promise<void> => {
    await apiClient.post(
      `/api/documents/${documentId}/e2ee/envelopes`,
      body
    );
  },

  /**
   * Walks every (document_id, file_id) pair for the member. Used
   * by ShareAccept to know which documents/files to attempt
   * re-wrapping for, and by the owner-side rewrap flow when
   * materialising link envelopes after a fresh invite.
   */
  listMemberE2eeIds: async (memberId: number): Promise<MemberE2eeIdsResponse> => {
    const response = await apiClient.get<MemberE2eeIdsResponse>(
      `/api/documents/member/${memberId}/e2ee/ids`
    );
    return response.data;
  },
};
